// src/pages/Admin/AdminSystemConfig/AdminSystemConfig.jsx
import React, { useState, useEffect } from 'react';
import AdminSidebar from '../../../components/AdminSidebar/AdminSidebar';
import { getApiUrl } from '../../../config/api';
import './AdminSystemConfig.css';

const AdminSystemConfig = ({ isUnderMaintenance, toggleMaintenanceMode }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [config, setConfig] = useState({
    maintenanceMode: false,
    maintenanceMessage: '',
    maintenanceEndTime: '',
    maintenanceTitle: '',
    allowAdminAccess: false,
    showProgressBar: false,
    maintenanceProgress: 0
  });

  // Función para obtener la configuración del sistema
  const fetchSystemConfig = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/system-config'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      } else {
        setError('Error al cargar la configuración');
      }
    } catch (err) {
      console.error('Error fetching config:', err);
      setError('Error de conexión al servidor: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Función para guardar la configuración
  const saveSystemConfig = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/system-config'), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(config)
      });

      if (response.ok) {
        setSuccess('Configuración guardada correctamente');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Error al guardar la configuración');
      }
    } catch (err) {
      console.error('Error saving config:', err);
      setError('Error de conexión al servidor: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Manejar cambios en los campos
  const handleInputChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Manejar toggle del modo mantenimiento
  const handleMaintenanceToggle = async () => {
    setLoading(true);
    try {
      await toggleMaintenanceMode();
      setConfig(prev => ({
        ...prev,
        maintenanceMode: !prev.maintenanceMode
      }));
    } catch (err) {
      setError('Error al cambiar el modo mantenimiento');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSystemConfig();
  }, []);

  useEffect(() => {
    setConfig(prev => ({
      ...prev,
      maintenanceMode: isUnderMaintenance
    }));
  }, [isUnderMaintenance]);

  return (
    <div className="admin-system-config">
      <AdminSidebar isUnderMaintenance={isUnderMaintenance} toggleMaintenanceMode={toggleMaintenanceMode} />
      
      <div className="admin-content">
        <div className="container-fluid">
          <div className="page-header">
            <h1 className="admin-page-title">Configuración de la Web</h1>
            <p>Administra la configuración general del sitio web</p>
          </div>

          {error && (
            <div className="alert alert-danger alert-dismissible">
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setError('')}
              ></button>
              {error}
            </div>
          )}

          {success && (
            <div className="alert alert-success alert-dismissible">
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setSuccess('')}
              ></button>
              {success}
            </div>
          )}

          {loading && (
            <div className="text-center my-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <p className="mt-2">Cargando...</p>
            </div>
          )}

          <div className="row justify-content-center">
            {/* Modo Mantenimiento */}
            <div className="col-lg-8 col-xl-6 mb-4">
              <div className="config-card">
                <div className="card-header">
                  <h5>🔧 Modo Mantenimiento</h5>
                </div>
                <div className="card-body">
                  <div className="maintenance-control">
                    <div className="maintenance-status">
                      <span className={`status-indicator ${isUnderMaintenance ? 'active' : 'inactive'}`}>
                        {isUnderMaintenance ? '🔴 ACTIVADO' : '🟢 DESACTIVADO'}
                      </span>
                    </div>
                    
                    <div className="form-group mt-3">
                      <label className="form-label">Mensaje de Mantenimiento</label>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={config.maintenanceMessage || ''}
                        onChange={(e) => handleInputChange('maintenanceMessage', e.target.value)}
                        placeholder="Mensaje que se mostrará durante el mantenimiento..."
                      />
                    </div>

                    <div className="form-group mt-3">
                      <label className="form-label">Tiempo Estimado de Finalización</label>
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={config.maintenanceEndTime || ''}
                        onChange={(e) => handleInputChange('maintenanceEndTime', e.target.value)}
                      />
                      <small className="form-text text-muted">
                        Opcional: Hora estimada de finalización del mantenimiento
                      </small>
                    </div>

                    <div className="form-group mt-3">
                      <label className="form-label">Título de la Página de Mantenimiento</label>
                      <input
                        type="text"
                        className="form-control"
                        value={config.maintenanceTitle || ''}
                        onChange={(e) => handleInputChange('maintenanceTitle', e.target.value)}
                        placeholder="Sitio en Mantenimiento"
                      />
                    </div>

                    <div className="form-group mt-3">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="allowAdminAccess"
                          checked={config.allowAdminAccess || false}
                          onChange={(e) => handleInputChange('allowAdminAccess', e.target.checked)}
                        />
                        <div>
                          <label className="form-check-label" htmlFor="allowAdminAccess">
                            Permitir Acceso a Administradores
                          </label>
                          <div className="form-check-description">
                            Los administradores pueden acceder al sitio durante el mantenimiento
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="form-group mt-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="showProgressBar"
                          checked={config.showProgressBar || false}
                          onChange={(e) => handleInputChange('showProgressBar', e.target.checked)}
                        />
                        <div>
                          <label className="form-check-label" htmlFor="showProgressBar">
                            Mostrar Barra de Progreso
                          </label>
                          <div className="form-check-description">
                            Muestra una barra de progreso visual en la página de mantenimiento
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="form-group mt-3">
                      <label className="form-label">Progreso del Mantenimiento (%)</label>
                      <input
                        type="range"
                        className="form-range"
                        min="0"
                        max="100"
                        value={config.maintenanceProgress || 0}
                        onChange={(e) => handleInputChange('maintenanceProgress', e.target.value)}
                        disabled={!config.showProgressBar}
                      />
                      <div className="d-flex justify-content-between">
                        <span>0%</span>
                        <span className="text-primary font-weight-bold">
                          {config.maintenanceProgress || 0}%
                        </span>
                        <span>100%</span>
                      </div>
                    </div>

                    <button
                      className={`btn ${isUnderMaintenance ? 'btn-danger' : 'btn-warning'} mt-3 w-100`}
                      onClick={handleMaintenanceToggle}
                      disabled={loading}
                    >
                      {isUnderMaintenance ? 'Desactivar Mantenimiento' : 'Activar Mantenimiento'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Botón para guardar toda la configuración */}
          <div className="row">
            <div className="col">
              <div className="config-card">
                <div className="card-body text-center">
                  <button
                    className="btn btn-success btn-lg"
                    onClick={saveSystemConfig}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        Guardando...
                      </>
                    ) : (
                      '💾 Guardar Configuración'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSystemConfig;